import React from 'react';
import { Trophy } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomDropdown from '../components/CustomDropdown';

const LeaderboardPage = ({
  testType,
  setTestType,
  difficulty,
  setDifficulty,
  testTypeOpen,
  setTestTypeOpen,
  difficultyOpen,
  setDifficultyOpen,
  leaderboardData,
  formatTime,
  setCurrentPage,
  goToLeaderboard,
  mobileMenuOpen,
  setMobileMenuOpen
}) => {
  const testTypeOptions = [
    { value: 'mental_math', label: 'Mental Math' },
    { value: 'sequences', label: 'Sequences' }
  ];

  const difficultyOptions = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' }
  ];

  const getLabelByValue = (value) => {
    const option = testTypeOptions.find(opt => opt.value === value);
    return option ? option.label : null;
  };

  const handleTestTypeChange = (newTestType) => {
    setTestType(newTestType);
    setTestTypeOpen(false);
  };

  const handleDifficultyChange = (newDifficulty) => {
    setDifficulty(newDifficulty);
    setDifficultyOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 flex flex-col overflow-x-hidden">
      <Header 
        setCurrentPage={setCurrentPage} 
        goToLeaderboard={goToLeaderboard}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <div className="flex-1 pt-24 sm:pt-28 md:pt-32 p-2 sm:p-4">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Leaderboard</h2>
              <p className="text-sm sm:text-base text-gray-600 capitalize">{getLabelByValue(testType)} - {difficulty}</p>
            </div>
            
            {/* Custom Selection Menu */}
            <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
              <CustomDropdown
                value={testType}
                options={testTypeOptions}
                onChange={handleTestTypeChange}
                isOpen={testTypeOpen}
                setIsOpen={setTestTypeOpen}
                placeholder="Select Test Type"
                minWidth="120px"
              />

              <CustomDropdown
                value={difficulty}
                options={difficultyOptions}
                onChange={handleDifficultyChange}
                isOpen={difficultyOpen}
                setIsOpen={setDifficultyOpen}
                placeholder="Select Difficulty"
                minWidth="100px"
              />
            </div>
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="min-w-full inline-block align-middle">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm">Rank</th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm">Name</th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm">Score</th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm hidden sm:table-cell">Accuracy</th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm hidden md:table-cell">Time</th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm hidden lg:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((entry, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm">
                        {entry.rank <= 3 ? (
                          <span className="flex items-center">
                            <Trophy className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 ${
                              entry.rank === 1 ? 'text-yellow-500' :
                              entry.rank === 2 ? 'text-gray-400' : 'text-amber-600'
                            }`} />
                            #{entry.rank}
                          </span>
                        ) : (
                          `#${entry.rank}`
                        )}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm">{entry.username}</td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm">{entry.score}/{entry.total_questions}</td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm hidden sm:table-cell">{entry.accuracy}%</td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm hidden md:table-cell">{formatTime(entry.time_taken)}</td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs text-gray-500 hidden lg:table-cell">{entry.submitted_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {leaderboardData.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm sm:text-base">
              No scores yet. Be the first to submit!
            </div>
          )}
        </div>
      </div>
      <div className="h-32"></div>
      <Footer setCurrentPage={setCurrentPage} goToLeaderboard={goToLeaderboard} />
    </div>
  );
};

export default LeaderboardPage;